import { createClient } from '@supabase/supabase-js'

// Use environment variables - fail if not set (security best practice)
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing required Supabase environment variables. Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in your .env file.'
  );
}

// Dedupe GET /auth/v1/user so only one request is sent (Supabase init + any app code).
// We must clone the response as soon as it arrives (before any body read) so deduped callers get a readable clone.
const AUTH_USER_URL_SUFFIX = '/auth/v1/user';
let authUserFetchPromise = null;

function clearAuthUserPromise() {
  authUserFetchPromise = null;
}

// Create Supabase client with custom fetch to intercept and log errors
const customFetch = async (url, options = {}) => {
  const isGetUser = options.method === 'GET' && typeof url === 'string' && url.endsWith(AUTH_USER_URL_SUFFIX);
  if (isGetUser && authUserFetchPromise) {
    return authUserFetchPromise;
  }

  const maxRetries = 3;
  let lastError;

  // Determine timeout based on endpoint type
  // Auth endpoints may need more time, especially after project restore
  const isAuthEndpoint = url.includes('/auth/v1/');
  const timeout = isAuthEndpoint ? 60000 : 30000; // 60s for auth, 30s for others

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Add timeout to prevent hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      const fetchOptions = {
        ...options,
        signal: controller.signal,
        // Ensure CORS headers are included
        mode: 'cors',
        credentials: 'omit',
      };

      let fetchPromise = fetch(url, fetchOptions);
      if (isGetUser) {
        // Clone as soon as response arrives (before first caller reads body) so deduped callers get a readable clone.
        // Keep for 5s so Strict Mode remount / delayed effects don't trigger a second request.
        authUserFetchPromise = fetchPromise.then((response) => response.clone());
        authUserFetchPromise.finally(() => setTimeout(clearAuthUserPromise, 5000));
      }
      const response = await fetchPromise;
      clearTimeout(timeoutId);
      
      // Check for invalid API key errors (usually 400 or 401 with specific message)
      if (response.status === 400 || response.status === 401) {
        // Try to read the response body to check for API key errors
        const clonedResponse = response.clone();
        clonedResponse.text().then(text => {
          try {
            const errorData = JSON.parse(text);
            
            if (errorData.message && errorData.message.includes('Invalid API key')) {
              console.error('🚨 INVALID API KEY ERROR:');
              console.error('   The Supabase anon key is invalid or expired.');
              console.error('   Please update your API key:');
              console.error('   1. Go to https://supabase.com/dashboard');
              console.error('   2. Select your project: zgiszibrpcfnixelehrp');
              console.error('   3. Navigate to Settings > API');
              console.error('   4. Copy the "anon" or "public" key');
              console.error('   5. Update REACT_APP_SUPABASE_ANON_KEY in your .env file');
              console.error('   6. Restart your development server');
              console.error('');
              console.error('   Current key being used:', supabaseAnonKey.substring(0, 50) + '...');
              console.error('   Error details:', errorData);
            }
          } catch (e) {
            // If we can't parse the error, that's fine
          }
        }).catch(() => {
          // Ignore errors reading the response
        });
        
        // For auth endpoints, log a warning
        if (isAuthEndpoint) {
          console.warn('🔒 Authentication failed (401): Invalid credentials');
        }
        
        return response; // Return the response so Supabase can handle it properly
      }
      
      // Log 406 errors specifically
      if (response.status === 406) {
        // Clone the response so we can read it without consuming the original
        const clonedResponse = response.clone();
        
        console.error('🚨 406 Not Acceptable Error Detected:', {
          url: url,
          method: options.method || 'GET',
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          requestHeaders: options.headers ? Object.fromEntries(new Headers(options.headers).entries()) : {},
          timestamp: new Date().toISOString()
        });
        
        // Try to get response body for more details (use cloned response)
        try {
          const text = await clonedResponse.text();
          console.error('406 Error Response Body:', text);
          
          // Try to parse as JSON if possible
          try {
            const json = JSON.parse(text);
            console.error('406 Error Response JSON:', json);
          } catch (e) {
            // Not JSON, that's fine
          }
        } catch (e) {
          // Ignore if we can't read the body
        }
      }
      
      return response;
    } catch (error) {
      lastError = error;
      
      // Log detailed error information
      const errorInfo = {
        url: url,
        method: options.method || 'GET',
        error: error.message,
        errorName: error.name,
        attempt: attempt,
        maxRetries: maxRetries,
        timestamp: new Date().toISOString()
      };
      
      // Check for specific error types
      if (error.name === 'AbortError') {
        console.error('⏱️ Request Timeout:', errorInfo);
        if (isAuthEndpoint) {
          console.error('💡 Auth endpoint timeout - this may indicate:');
          console.error('   1. Supabase project is still starting up (wait a few minutes)');
          console.error('   2. Network connectivity issues');
          console.error('   3. Supabase service is experiencing high load');
        }
      } else if (error.message.includes('Failed to fetch') || error.message.includes('Load failed')) {
        console.error('🌐 Network/CORS Error:', errorInfo);
        console.error('💡 Possible causes:');
        console.error('   1. Supabase project might be paused (check Supabase dashboard)');
        console.error('   2. Network connectivity issue');
        console.error('   3. CORS restrictions in Supabase settings');
        console.error('   4. Browser blocking the request');
        console.error('   5. Check if Supabase project is active at: https://supabase.com/dashboard');
      } else {
        console.error('❌ Network Error in Supabase Request:', errorInfo);
      }
      
      // If this is the last attempt, throw the error
      if (attempt === maxRetries) {
        // For auth endpoints, provide more helpful error message
        if (isAuthEndpoint && error.name === 'AbortError') {
          const authError = new Error('Authentication request timed out. Please check your credentials and try again. If the problem persists, the Supabase project may still be starting up.');
          authError.name = 'AuthTimeoutError';
          throw authError;
        }
        throw error;
      }
      
      // Wait before retrying (exponential backoff)
      // Longer wait for auth endpoints
      const backoffDelay = isAuthEndpoint 
        ? Math.min(2000 * Math.pow(2, attempt - 1), 10000) // 2s, 4s, 8s for auth
        : Math.min(1000 * Math.pow(2, attempt - 1), 5000); // 1s, 2s, 4s for others
      await new Promise(resolve => setTimeout(resolve, backoffDelay));
    }
  }
  
  // This should never be reached, but TypeScript needs it
  throw lastError || new Error('Unknown error in customFetch');
};
 
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  global: {
    fetch: customFetch
  },
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true
  }
})
